import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Check, ArrowRight, Calendar as CalendarIcon, Clock, BookOpen, MessageCircle } from "lucide-react";

const PLAYBOOK_URL = "/The-Client-Acquisition-Funnel-BrandChilly.pdf";

function fmtDateLong(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function BookingConfirmedPage() {
  const { state } = useLocation();
  const confirmation = state?.booking || null;
  const getPlaybook = () => {
    try { window.open(PLAYBOOK_URL, "_blank", "noopener"); } catch (e) { /* noop */ }
  };

  useEffect(() => {
    document.title = "Booking Confirmed — BrandChilly";

    // FIRE CONVERSION EVENTS HERE (so they fire on /confirmed URL — custom Meta conversion can target this path)
    try {
      const params = confirmation
        ? { content_name: "BookingConfirmed", date: confirmation.date, time: confirmation.time, value: 1, currency: "INR" }
        : { content_name: "BookingConfirmed", value: 1, currency: "INR" };
      if (window.fbq) {
        window.fbq("track", "Schedule", params);
        window.fbq("track", "Lead", params);
      }
      if (window.gtag) {
        window.gtag("event", "schedule", params);
        window.gtag("event", "generate_lead", params);
      }
      if (window.dataLayer) window.dataLayer.push({ event: "booking_confirmed", booking: confirmation || null });
    } catch (e) {
      // swallow
    }
  }, [confirmation]);

  return (
    <div className="coaches-page" style={{ minHeight: "100vh", background: "var(--c-bg)" }}>
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-14 md:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="confirmed-logo">
            <img src="/assets/logo.png" alt="BrandChilly" className="h-8 md:h-10 w-auto" />
          </Link>
          <div className="text-[11px] c-muted tracking-[0.18em] uppercase hidden sm:block">
            All times in IST
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="text-center max-w-xl mx-auto" data-testid="booking-success">
          <div
            className="inline-flex items-center justify-center w-16 h-16 border border-red-600 c-red mb-7"
            style={{ borderRadius: 0 }}
          >
            <Check size={28} strokeWidth={2.2} />
          </div>
          <div className="c-eyebrow mb-3">Booking Confirmed</div>
          <h1 className="text-3xl md:text-5xl leading-[1.05]" data-testid="confirmed-heading">
            Your booking is confirmed!
          </h1>

          {confirmation ? (
            <div
              className="mt-6 inline-block border border-white/10 px-6 py-4"
              style={{ borderRadius: 0 }}
            >
              <div className="text-xs c-muted tracking-[0.18em] uppercase mb-1">Your slot</div>
              <div
                className="text-xl md:text-2xl text-white flex items-center justify-center gap-2"
                style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}
              >
                <CalendarIcon size={16} className="c-red" /> {fmtDateLong(confirmation.date)}
              </div>
              <div className="text-base c-red mt-1 flex items-center justify-center gap-1.5">
                <Clock size={14} /> {confirmation.time} IST
              </div>
            </div>
          ) : null}

          <div
            className="mt-9 text-left border border-emerald-600/40 p-6 md:p-7 flex items-start gap-4"
            style={{ borderRadius: 0, background: "linear-gradient(180deg,#0c1512 0%,#070b09 100%)" }}
            data-testid="whatsapp-sent-note"
          >
            <MessageCircle size={22} style={{ color: "#34d399" }} className="shrink-0 mt-1" />
            <div>
              <div className="c-eyebrow mb-2" style={{ color: "#34d399" }}>Check your WhatsApp</div>
              <p className="c-muted text-sm md:text-base leading-relaxed">
                We've sent your booking confirmation on WhatsApp, along with a short 5-minute video from
                Saurabh. Watch it before the call so we can go straight to building your growth engine.
              </p>
            </div>
          </div>

          {/* PLAYBOOK */}
          <div
            className="mt-6 text-left border border-red-700/40 p-6 md:p-7"
            style={{ borderRadius: 0, background: "linear-gradient(180deg,#1a0d0f 0%,#0b0708 100%)" }}
            data-testid="playbook-step"
          >
            <div className="c-eyebrow mb-2">Come prepared</div>
            <h2
              className="text-2xl md:text-3xl text-white leading-tight"
              style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}
            >
              Go through <span className="c-red">The Client Acquisition Funnel</span> Playbook
            </h2>
            <p className="mt-3 c-muted text-sm md:text-base leading-relaxed">
              This is the exact system we use to help you hit your goal. Read it before your call so we
              can skip the basics and go straight to building your growth engine. ~10 minutes, well spent.
            </p>
            <button
              onClick={getPlaybook}
              className="c-btn-primary inline-flex mt-5"
              data-testid="get-playbook-btn"
            >
              Get the Playbook <BookOpen size={16} />
            </button>
          </div>

          <p className="mt-7 c-muted text-sm md:text-base leading-relaxed">
            We've also sent a confirmation to your email with all the details. Add the call to your
            calendar so it doesn't slip. See you soon.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/" className="btn-ghost inline-flex" data-testid="booking-back-home">
              Back to home <ArrowRight size={16} />
            </Link>
            {confirmation?.cancellation_token && (
              <a
                href={`/book/cancel?token=${confirmation.cancellation_token}`}
                className="text-xs c-muted underline hover:text-white"
                data-testid="booking-cancel-link"
              >
                Need to cancel? Use this link
              </a>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
